import {
    useGetScoreListWithContextQuery,
    type ScoreListResponse,
} from '@fiji/common/src/features/prediction/predictionApi';
import { Stack, Avatar, Typography, List, ListItem, Box } from '@mui/material';
import { CustomModal, CustomTransComponent } from 'components';
import { ModalActions } from 'components/CustomModal';
import React, { type JSX } from 'react';
import { UPCOMING_EVENTS_DETAILS_TEST_IDS } from 'pages/UpcomingEventsDetails/testIds';

const SCORE_MODAL_PAPER_SX = {
    width: '612px !important',
    maxWidth: '612px !important',
    borderRadius: '4px',
    boxShadow:
        '0px 7px 8px -4px rgba(0, 0, 0, 0.2), 0px 12px 17px 2px rgba(66, 78, 84, 0.14), 0px 5px 22px 4px rgba(0, 0, 0, 0.12)',
} as const;

// Keys are normalized labels; the API exposes no stable parameter id yet.
const SCORE_PARAMETER_TRANSLATION_KEYS: Record<string, string> = {
    'device mode': 'UPCOMING_EVENTS:PULSE_SCORE_MODAL_DEVICE_MODE',
    'events and alarms by severity': 'UPCOMING_EVENTS:PULSE_SCORE_MODAL_EVENTS_AND_ALARMS',
    'device health data': 'UPCOMING_EVENTS:PULSE_SCORE_MODAL_DEVICE_HEALTH_DATA',
    'historical data': 'UPCOMING_EVENTS:PULSE_SCORE_MODAL_HISTORICAL_DATA',
    'configuration, age, best practices': 'UPCOMING_EVENTS:PULSE_SCORE_MODAL_CONFIGURATION_AGE_BEST_PRACTICES',
};

const normalizeScoreLabel = (label: string): string => label.trim().replace(/\s+/g, ' ').toLowerCase();

export type ScoreModalHandle = {
    onClick: () => void;
};

type ScoreModalProps = {
    'data-testid'?: string;
    'aria-label'?: string;
    realmPrefix?: string;
    organizationId?: string;
};

const ScorePoint = ({ value, testId }: { value: number; testId: string }): JSX.Element => (
    <Box
        data-testid={testId}
        sx={{
            alignItems: 'center',
            border: '1px solid #B9BFC2',
            borderRadius: '4px',
            display: 'flex',
            height: 32,
            justifyContent: 'center',
            width: 35,
        }}
    >
        <Typography sx={{ color: '#424E54', fontSize: 16, lineHeight: '16px' }}>{`${value}%`}</Typography>
    </Box>
);

export const ScoreModal = React.forwardRef<ScoreModalHandle, ScoreModalProps>((props, ref): JSX.Element => {
    const [openPulseScore, setOpenPulseScore] = React.useState(false);
    const { data: scoreListData, isLoading } = useGetScoreListWithContextQuery(
        { realmPrefix: props.realmPrefix, organizationId: props.organizationId },
        { skip: !openPulseScore }
    );
    const scoreGrades: ScoreListResponse['grades'] = scoreListData?.grades ?? [];
    const scoreRows = (scoreListData?.parameters ?? []).map((parameter, index) => ({
        key: `${parameter.label}-${index}`,
        label: parameter.label,
        translationKey: SCORE_PARAMETER_TRANSLATION_KEYS[normalizeScoreLabel(parameter.label)],
        points: parameter.value,
    }));
    const pulseScoreActions: ModalActions[] = [
        {
            key: 'ok',
            label: <CustomTransComponent translationKey="COMMON:OK" />,
            close: true,
            variant: 'contained',
            sx: { height: 36, minWidth: 68, px: 2 },
            testId: UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.okButton,
            handleClick: (): void => {
                setOpenPulseScore(false);
            },
        },
    ];

    React.useImperativeHandle(ref, () => ({
        onClick: (): void => setOpenPulseScore(true),
    }));

    return (
        <CustomModal
            isOpen={openPulseScore}
            isLoading={isLoading}
            noDividers
            hidePadding
            type="medium"
            actions={pulseScoreActions}
            paperSx={SCORE_MODAL_PAPER_SX}
            footerClass="score-modal__footer"
            header={<CustomTransComponent translationKey="UPCOMING_EVENTS:PULSE_SCORE" />}
            data-testid={props['data-testid'] ?? UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.root}
            aria-label={props['aria-label'] ?? UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.root}
        >
            <>
                <Stack
                    sx={{ px: 3 }}
                    data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.gradesRow}
                    aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.gradesRow}
                >
                    <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ opacity: 0.8, py: 1 }}>
                        {scoreGrades.map(
                            (item, index): JSX.Element => (
                                <Avatar
                                    key={`${item.grade}-${item.minScore ?? ''}-${item.maxScore ?? ''}-${index}`}
                                    sx={{ backgroundColor: item.color, height: 40, width: 40 }}
                                    data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.gradeBadge(
                                        item.grade,
                                        index
                                    )}
                                    aria-label={`score-grade-${item.grade.toLowerCase()}`}
                                >
                                    <Typography
                                        className="text-white"
                                        fontWeight={'700'}
                                        fontSize={'20px'}
                                        textAlign={'center'}
                                    >
                                        {item.grade}
                                    </Typography>
                                </Avatar>
                            )
                        )}
                    </Stack>
                    <Typography
                        variant="body1"
                        className="black-500"
                        sx={{ mt: 1, fontSize: 16, lineHeight: '22px' }}
                        data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightedMethodText}
                        aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightedMethodText}
                    >
                        <CustomTransComponent
                            translationKey="UPCOMING_EVENTS:PULSE_SCORE_MODAL_WEIGHTED_METHOD"
                            components={{ bold: <span className="fw-700 f-16" /> }}
                        />
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ color: '#424E54', fontSize: 16, lineHeight: '22px', mt: 1.25 }}
                        data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.groupScoreText}
                    >
                        <Box component="span" sx={{ fontWeight: 600 }}>
                            Group PulseScore™
                        </Box>{' '}
                        is based on the scores of the devices and groups within a group.
                    </Typography>
                </Stack>
                <List
                    disablePadding
                    sx={{ py: 1 }}
                    data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightsList}
                    aria-label={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightsList}
                >
                    {scoreRows.map((row, index) => (
                        <ListItem
                            key={row.key}
                            data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightRow(row.label, index)}
                            sx={{
                                bgcolor: index % 2 === 1 ? '#FBFBFB' : '#FFFFFF',
                                justifyContent: 'space-between',
                                minHeight: 38,
                                px: 3,
                                py: 0,
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{ color: '#424E54', fontSize: 16 }}
                                data-testid={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightLabel(row.label, index)}
                            >
                                {row.translationKey ? (
                                    <CustomTransComponent translationKey={row.translationKey} />
                                ) : (
                                    row.label
                                )}
                            </Typography>
                            <ScorePoint
                                value={row.points}
                                testId={UPCOMING_EVENTS_DETAILS_TEST_IDS.scoreModal.weightPoints(row.label, index)}
                            />
                        </ListItem>
                    ))}
                </List>
            </>
        </CustomModal>
    );
});
